import { FeePortfolioDetailDTO } from "@models/platform-analysis/entities/portfolioDetails";
import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { useTheme } from "@mui/material";
import { PDFTable } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables";
import { PDFTableBody } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-body";
import { PDFTableCell } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-cell";
import { PDFTableRow } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-row";
import { HorizontalLine } from "@pages/platform-analysis/components/buttons/export-pdf/components/line";
import {
  SUB_TITLE_HEIGHT,
  TABLE_CELL_WIDTH,
  TABLE_FIRST_CELL_WIDTH,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import { useStyles } from "@pages/platform-analysis/components/buttons/export-pdf/styles";
import {
  displayIntNumber,
  getPortfolioDetails,
  getStringFamilyMemberAccounts,
  isAllExpandEssentialSubProductsHidden,
} from "@pages/platform-analysis/util";
import { FeePortfolioDetailsConstants } from "@pages/platform-analysis/_id/steps/summary/constants";
import { Text, View } from "@react-pdf/renderer";
import { formatCurrencyWithFixedDecimals } from "@utils";

export interface PDFFeePortfolioDetailsSectionProps {
  data?: FeePortfolioDetailDTO | null;
  isShowHoldingsNumber?: boolean;
  portfolioSetup?: FeeCalculatorPortfolioLevel;
  hasNoPortfolioLevel?: boolean;
  isPlatformAnalysis?: boolean;
  hiddenSubProductIds?: string[];
  selectedSubProducts?: FeeSubProductDTO[];
}

export const PDFFeePortfolioDetailsSection = ({
  data,
  isShowHoldingsNumber,
  portfolioSetup = FeeCalculatorPortfolioLevel.No,
  hasNoPortfolioLevel = portfolioSetup === FeeCalculatorPortfolioLevel.No,
  isPlatformAnalysis,
  hiddenSubProductIds = [],
  selectedSubProducts = [],
}: PDFFeePortfolioDetailsSectionProps) => {
  const { typographyStyle } = useStyles();
  const { palette } = useTheme();

  const {
    transactionsOutside,
    transactionsWithin,
    investments,
    cfsBalances,
    expandBalances,
    northBalances,
  } = getPortfolioDetails(data);

  const retailInsurancePolicyInvestments = investments.filter((investment) =>
    investment.match(FeePortfolioDetailsConstants.RetailInsurancePoliciesRegex),
  );

  const showCfsBalances = cfsBalances.length > 0;
  const showNorthBalances = northBalances.length > 0;
  const showExpandBalances =
    !isAllExpandEssentialSubProductsHidden({
      selectedSubProducts,
      hiddenSubProductIds,
    }) && expandBalances.length > 0;

  return (
    <>
      <View style={{ height: SUB_TITLE_HEIGHT }}>
        <Text
          style={{
            ...typographyStyle.title,
            color: palette.common.black,
          }}
        >
          {isPlatformAnalysis ? "Fee analysis" : "Fee review"}
        </Text>
        <HorizontalLine color={palette.common.black} />
        <Text
          style={{
            ...typographyStyle.subtitle,
            color: palette.common.black,
          }}
        >
          Portfolio details for fee estimates
        </Text>
      </View>

      <PDFTable>
        <PDFTableBody>
          {/* Family members */}
          {data?.familyMembers?.map((familyMember, familyMemberIndex) => {
            const stringFamilyMemberAccounts = getStringFamilyMemberAccounts(
              familyMember.familyMemberAccounts,
            );
            return (
              <PDFTableRow key={familyMemberIndex}>
                <PDFTableCell
                  width={TABLE_FIRST_CELL_WIDTH}
                  alignItems={"flex-start"}
                >
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      fontWeight: "bold",
                      lineHeight: 1.2,
                    }}
                  >
                    {familyMember.name}
                  </Text>
                </PDFTableCell>
                <PDFTableCell
                  width={TABLE_CELL_WIDTH * 6}
                  alignItems={"flex-start"}
                >
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      lineHeight: 1.2,
                    }}
                  >
                    {stringFamilyMemberAccounts}
                  </Text>
                </PDFTableCell>
              </PDFTableRow>
            );
          })}

          {/* Total portfolio value */}
          {data?.totalPortfolioValue && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    lineHeight: 1.2,
                  }}
                >
                  {FeePortfolioDetailsConstants.TotalPortfolioValue}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    maxLines: 3,
                    lineHeight: 1.2,
                  }}
                >
                  {formatCurrencyWithFixedDecimals({
                    value: data?.totalPortfolioValue,
                  })}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* CFS Edge balances */}
          {showCfsBalances && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  {FeePortfolioDetailsConstants.BalanceInCFSEdgeMenus}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {cfsBalances.join(", ")}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* Expand Essential balances */}
          {showExpandBalances && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  {FeePortfolioDetailsConstants.BalanceInExpandEssentialMenus}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {expandBalances.join(", ")}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* North balances */}
          {showNorthBalances && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  {FeePortfolioDetailsConstants.BalanceInNorthMenus}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {northBalances.join(", ")}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* Investments and other holdings */}
          {investments.length && hasNoPortfolioLevel && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    lineHeight: 1.2,
                  }}
                >
                  {FeePortfolioDetailsConstants.InvestmentsAndOtherHoldings}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {investments.join(", ")}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* Total different investments held */}
          {isShowHoldingsNumber && data?.totalDifferentInvestments && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    lineHeight: 1.2,
                  }}
                >
                  {FeePortfolioDetailsConstants.TotalDifferentInvestmentsHeld}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {displayIntNumber(data?.totalDifferentInvestments)}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* Transactions outside managed accounts */}
          {transactionsOutside.length && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    textAlign: "left",
                    fontWeight: "bold",
                    lineHeight: 1.2,
                  }}
                >
                  {`Transactions `}
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      textDecoration: "underline",
                      fontWeight: "bold",
                      lineHeight: 1.2,
                    }}
                  >
                    outside
                  </Text>
                  {` managed accounts (Next 12 months)`}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {transactionsOutside.join(", ")}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* Transactions within managed accounts */}
          {transactionsWithin.length && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  {`Transactions `}
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      textDecoration: "underline",
                      fontWeight: "bold",
                      lineHeight: 1.2,
                    }}
                  >
                    within
                  </Text>
                  {` managed accounts (Next 12 months)`}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {transactionsWithin.join(", ")}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}

          {/* Number of retail insurance polices */}
          {retailInsurancePolicyInvestments.length > 0 && !hasNoPortfolioLevel && (
            <PDFTableRow>
              <PDFTableCell
                width={TABLE_FIRST_CELL_WIDTH}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    fontWeight: "bold",
                    textAlign: "left",
                    lineHeight: 1.2,
                  }}
                >
                  {FeePortfolioDetailsConstants.NumberOfRetailInsurancePolices}
                </Text>
              </PDFTableCell>
              <PDFTableCell
                width={TABLE_CELL_WIDTH * 6}
                alignItems={"flex-start"}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    lineHeight: 1.2,
                  }}
                >
                  {retailInsurancePolicyInvestments.join(", ")}
                </Text>
              </PDFTableCell>
            </PDFTableRow>
          )}
        </PDFTableBody>
      </PDFTable>
    </>
  );
};
